// 1 product on Products (AdminProducts.dc.html): its cover, name, type and Live or Draft, the "now"
// line, its prices, the panel buttons (Welcome checklist, Certificate, Page layout, Duplicate, and
// Settings, which no board draws), See it as a member, the manage button and the Live switch, which
// asks first. Duplicate shows on every product, of every type (the product-types plan, 13 Oct), and on
// the free linked items and resource packs; never on Free webinars, which holds every webinar. A
// product that hasn't moved in (POYT and the Accelerator on live in phase 1) shows only where it lives:
// no Live switch, checklist, certificate, Page layout or Duplicate (the critic's missed 2).
import { useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { ButtonLink, ConfirmPanel, useAdmin } from '../ui/index.js'
import { PRODUCTS } from './copy.js'
import { typeLabel, isMovedIn, outsideHost, hasLayout, manageLink, checklistFor, contrastCheck, lookColours, canDuplicate } from './model.js'
import { setState } from './data.js'
import { Cover, BigSwitch, Chip } from './bits.jsx'
import { ChecklistPanel } from './ChecklistPanel.jsx'
import { CertificatePanel } from './CertificatePanel.jsx'

// The Live switch's question, for paid and free alike
function confirmWords(p, on) {
  if (p.access_kind === 'free') return on ? PRODUCTS.confirmOnFree(p.name) : PRODUCTS.confirmOffFree(p.name)
  return on ? PRODUCTS.confirmOn(p.name) : PRODUCTS.confirmOff(p.name)
}

// Why a product can't go live, or null. Members' pages can't draw an own look yet, so none goes live
// (Adam, 5 Oct, question 8; guard_catalogue refuses it too). Once they can, an own look must still pass
// its contrast check (catalogue_own_look_live).
export const OWN_LOOK_DRAWN = false
export function ownLookBlocks(p) {
  if (p.look !== 'own') return null
  if (!OWN_LOOK_DRAWN) return PRODUCTS.ownLookNotDrawn
  return p.own_look_contrast_ok && contrastCheck(lookColours('own', p.own_look)).pass ? null : PRODUCTS.ownLookFails
}

// Switching a product on or off also reloads admin's own list of products, which People's Open a 1-2-1
// space and the access card read: a second private client space switched on here is offered there at
// once, with no reload of the page
export function useLiveSwitch({ api, product, onChange }) {
  const reloadProducts = useAdmin()?.reloadProducts
  const [asking, setAsking] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const on = product.state === 'live'
  const blocked = on ? null : ownLookBlocks(product)
  const flip = () => {
    setError(null)
    if (blocked) { setAsking('blocked'); return }
    setAsking(on ? 'off' : 'on')
  }
  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const row = await setState(api, product.id, asking === 'on' ? 'live' : 'draft')
      onChange({ state: row.state })
      reloadProducts?.()
      setAsking(null)
    } catch (e) { setError(e) } finally { setBusy(false) }
  }
  return {
    control: <BigSwitch checked={on} label={on ? PRODUCTS.switchOff(product.name) : PRODUCTS.switchOn(product.name)} onClick={flip} disabled={busy} />,
    panel: asking === 'blocked'
      ? <div className="ap-confirm-wrap"><div className="ad-confirm"><span className="ad-confirm-text" role="alert">{blocked}</span>
        <span className="ad-confirm-actions"><button type="button" className="ad-btn ad-btn--line ad-btn--sm" onClick={() => setAsking(null)}>{PRODUCTS.close}</button></span></div></div>
      : asking
        ? <div className="ap-confirm-wrap"><ConfirmPanel text={confirmWords(product, asking === 'on')} busy={busy} error={error}
            confirmLabel={asking === 'on' ? PRODUCTS.switchItOn : PRODUCTS.switchItOff} cancelLabel={asking === 'on' ? PRODUCTS.notYet : PRODUCTS.keepItOn}
            onConfirm={confirm} onCancel={() => { setAsking(null); setError(null) }} /></div>
        : null,
  }
}

export function ProductCard({ api, product: p, data, cover, open, onOpen, onChange, onChecklist, onDuplicate, duplicating, onSeeAs, prices }) {
  const moved = isMovedIn(p)
  const live = p.state === 'live'
  const items = checklistFor(p.id, data.checklist)
  const manage = manageLink(p, data)
  const sw = useLiveSwitch({ api, product: p, onChange })
  const panelId = kind => `ap-${kind}-${p.id}`
  const host = outsideHost(p)

  return (
    <article className="ap-prod" aria-labelledby={`ap-name-${p.id}`}>
      <div className={`ap-prod-row${live || !moved ? '' : ' ap-prod-row--draft'}`}>
        <Cover product={p} url={cover} />
        <div className="ap-prod-main">
          <span className="ap-prod-title">
            <h3 className="ap-prod-name" id={`ap-name-${p.id}`}>{p.name}</h3>
            <Chip>{typeLabel(p.type)}</Chip>
            {moved ? <Chip kind={live ? 'live' : 'draft'}>{live ? PRODUCTS.live : PRODUCTS.draft}</Chip> : <Chip kind="draft">{PRODUCTS.notMovedIn}</Chip>}
          </span>
          <span className="ap-prod-now">{data.nowLines[p.id]}</span>
          {moved && <span className="ap-prod-prices">{prices}</span>}
          {moved && (
            <span className="ap-prod-buttons">
              <button type="button" className={`ad-btn ad-btn--line ad-btn--xs${open === 'check' ? ' ap-pressed' : ''}`} aria-expanded={open === 'check'}
                aria-controls={open === 'check' ? panelId('check') : undefined} onClick={() => onOpen(open === 'check' ? null : 'check')}>
                {PRODUCTS.checklist(items.length)}
              </button>
              <button type="button" className={`ad-btn ad-btn--line ad-btn--xs${open === 'cert' ? ' ap-pressed' : ''}`} aria-expanded={open === 'cert'}
                aria-controls={open === 'cert' ? panelId('cert') : undefined} onClick={() => onOpen(open === 'cert' ? null : 'cert')}>
                {PRODUCTS.certificate(p.certificate_on)}
              </button>
              {hasLayout(p) && <Link to={`/admin/products/${p.slug}/layout`} className="ad-btn ad-btn--line ad-btn--xs">{PRODUCTS.layout}</Link>}
              {canDuplicate(p) && <button type="button" className="ad-btn ad-btn--line ad-btn--xs" disabled={duplicating} aria-busy={duplicating || undefined} onClick={onDuplicate}>{PRODUCTS.duplicate}</button>}
              <Link to={`/admin/products/${p.slug}`} className="ad-btn ad-btn--line ad-btn--xs">{PRODUCTS.settings}</Link>
            </span>
          )}
        </div>
        <div className="ap-prod-side">
          {moved
            ? <button type="button" className="ad-btn ad-btn--line ap-btn-36" onClick={onSeeAs}>{PRODUCTS.seeAsMember}</button>
            : <ButtonLink to={`https://${host}`} className="ap-btn-36">{PRODUCTS.openSite(host)}</ButtonLink>}
          {moved && <ButtonLink to={manage.to} kind="ink" className="ap-btn-36">{manage.label}</ButtonLink>}
          {moved && sw.control}
        </div>
      </div>
      {sw.panel}
      {moved && open === 'check' && <ChecklistPanel api={api} product={p} items={items} onChange={onChecklist} id={panelId('check')} />}
      {moved && open === 'cert' && <CertificatePanel api={api} product={p} onChange={row => onChange(row)} id={panelId('cert')} />}
    </article>
  )
}

export function FreeCard({ api, product: p, data, cover, onChange, onDuplicate, duplicating }) {
  const live = p.state === 'live'
  const manage = manageLink(p, data)
  const sw = useLiveSwitch({ api, product: p, onChange })
  const preview = p.type === 'webinar' ? '/free-webinars' : '/free-resources'
  return (
    <article className="ap-prod" aria-labelledby={`ap-name-${p.id}`}>
      <div className={`ap-prod-row${live ? '' : ' ap-prod-row--draft'}`}>
        <Cover product={p} url={cover} size="free" />
        <div className="ap-prod-main">
          <span className="ap-prod-title">
            <h3 className="ap-prod-name" id={`ap-name-${p.id}`}>{p.name}</h3>
            <Chip>{typeLabel(p.type)}</Chip>
            <Chip kind="free">{PRODUCTS.free}</Chip>
            <Chip kind={live ? 'live' : 'draft'}>{live ? PRODUCTS.live : PRODUCTS.draft}</Chip>
          </span>
          <span className="ap-prod-now ap-prod-what">{data.freeLines[p.id]}</span>
          {canDuplicate(p) && onDuplicate && (
            <span className="ap-prod-buttons">
              <button type="button" className="ad-btn ad-btn--line ad-btn--xs" disabled={duplicating} aria-busy={duplicating || undefined} onClick={onDuplicate}>{PRODUCTS.duplicate}</button>
            </span>
          )}
        </div>
        <div className="ap-prod-side">
          <ButtonLink to={preview} className="ap-btn-36">{PRODUCTS.seeAsMember}</ButtonLink>
          <ButtonLink to={manage.to} kind="ink" className="ap-btn-36">{manage.label}</ButtonLink>
          {sw.control}
        </div>
      </div>
      {sw.panel}
    </article>
  )
}
